import { useQuery } from '@tanstack/react-query'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  MapPin,
  Gauge,
  Clock,
  User,
  Wrench,
  MessageSquare,
  FileText,
  Loader2
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface VehicleDetails {
  id: string
  name: string
  license_plate: string
  status: 'active' | 'idle' | 'offline'
  driver_name?: string
  location: {
    address: string
  }
  speed: number
  last_update: number
  diagnostics: {
    odometer: number
  }
}

const statusConfig = {
  active: { color: 'bg-green-500', label: 'Active' },
  idle: { color: 'bg-yellow-500', label: 'Idle' },
  offline: { color: 'bg-gray-500', label: 'Offline' }
}

interface VehicleDetailsSheetProps {
  vehicleId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function VehicleDetailsSheet({ vehicleId, open, onOpenChange }: VehicleDetailsSheetProps) {
  const { data: vehicle, isLoading } = useQuery({
    queryKey: ['vehicle-details', vehicleId],
    queryFn: async () => {
      if (!vehicleId) return null
      const res = await fetch(`/api/fleet/vehicles/${vehicleId}`)
      if (!res.ok) throw new Error('Failed to fetch vehicle details')
      return res.json() as Promise<VehicleDetails>
    },
    enabled: !!vehicleId && open
  })

  if (!open) return null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : vehicle ? (
          <>
            <SheetHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <SheetTitle className="text-xl">{vehicle.name}</SheetTitle>
                  <SheetDescription>
                    License: {vehicle.license_plate}
                  </SheetDescription>
                </div>
                <Badge className={`${statusConfig[vehicle.status].color} text-white border-none`}>
                  {statusConfig[vehicle.status].label}
                </Badge>
              </div>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              <div className="space-y-3">
                {vehicle.driver_name && (
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Driver</p>
                      <p className="text-sm text-muted-foreground">{vehicle.driver_name}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Location</p>
                    <p className="text-sm text-muted-foreground">{vehicle.location.address}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Gauge className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">{vehicle.speed} mph</p>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Last update: {formatDistanceToNow(vehicle.last_update, { addSuffix: true })}
                  </p>
                </div>
              </div>

              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  DIAGNOSTICS
                </h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-muted-foreground text-sm">Odometer</p>
                    <p className="font-medium">{vehicle.diagnostics.odometer.toLocaleString()} mi</p>
                  </div>
                </div>
              </div>

              <Separator />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="w-full">
                  <MapPin className="h-4 w-4 mr-2" />
                  Track
                </Button>
                <Button variant="outline" className="w-full">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Message
                </Button>
                <Button variant="outline" className="w-full">
                  Details
                </Button>
                <Button variant="outline" className="w-full">
                  <FileText className="h-4 w-4 mr-2" />
                  DVIR
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Vehicle not found</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
